import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  StatsCustomMetricAddForm,
  StatsCustomMetricAddInitialValues,
  StatsCustomMetricComponents,
  StatsCustomMetricAdditionalParams,
  StatsCustomMetricFormOptions,
  onStatsCustomMetricFormSaved,
  IStatsCustomMetricFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateStatsCustomMetric, PermissionEnum, } from 'atlas-shared';
import { createStatsCustomMetric, fetchStatsCustomMetric, getStatsCustomMetricCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, useStatsCustomMetrics, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateStatsCustomMetric> {}

const StatsCustomMetricFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const stats_custom_metrics = useStatsCustomMetrics();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateStatsCustomMetric>>();

  const param_props: IStatsCustomMetricFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
    stats_custom_metrics: stats_custom_metrics.stats_custom_metrics,
  };
  const formLayout = useMemo(() => StatsCustomMetricAddForm(values, param_props, t), [values, organizations, stats_custom_metrics, t, abilities, ]);
  const options: IFormOptions = useMemo(() => StatsCustomMetricFormOptions(values || StatsCustomMetricAddInitialValues, param_props, t), [values, organizations, stats_custom_metrics, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateStatsCustomMetric, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateStatsCustomMetric) => save(() => createStatsCustomMetric(values).then(
    res => onSaved ? onSaved(res, navigate) : onStatsCustomMetricFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchStatsCustomMetric(+id)
        .then(({
          organization_id,
          title,
          query_type,
          metrics,
          description,
        }) => {
          setCloned({
            organization_id,
            title,
            query_type,
            metrics,
            description,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.StatsCustomMetric, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={StatsCustomMetricComponents}
        additionalParams={StatsCustomMetricAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...StatsCustomMetricAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('STATS_CUSTOM_METRIC')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const StatsCustomMetricFormAddView = React.memo(withAdd(StatsCustomMetricFormAdd, getStatsCustomMetricCreateSchema, StatsCustomMetricAddInitialValues));
